import { useParams, Link, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, MapPin, FileText } from "lucide-react";
import { IDS } from "@/lib/testIds";
import { useLang } from "@/lib/LangContext";
import { CASE_STUDIES } from "@/lib/insights";
import Seo from "@/components/Seo";
import QuoteForm from "@/components/QuoteForm";
import EditorialMarquee from "@/components/EditorialMarquee";

export default function CityDetail() {
  const { slug } = useParams();
  const { cities: CITIES, services: SERVICES, lang } = useLang();
  const city = CITIES.find((c) => c.slug === slug);
  if (!city) return <Navigate to="/cities" replace />;

  const nearby = CITIES.filter((c) => c.slug !== slug).slice(0, 6);
  const caseStudy = CASE_STUDIES.find((cs) => cs.citySlug === slug);

  const longContent = [
    `Security guard services in ${city.name} require deep local knowledge of the ${city.region} region — from ${city.landmarks.slice(0, 2).join(" and ")} to the everyday commercial corridors that carry the ${city.population}-strong community. New Brunswick Security Services deploys ${city.name}-based officers who know every industrial park, every event venue, and every response route in and out of the community.`,
    `Our ${city.name} team is licensed under the New Brunswick Private Investigators and Security Services Act, insured to $5,000,000, and available for uniformed guarding, mobile patrol, event security, alarm response and construction site protection at 45-second response initiation from dispatch. Whether you are a property manager, general contractor, event producer or private homeowner in ${city.name}, we scale to your requirement.`,
    `From routine overnight patrol contracts at ${city.landmarks[0] || "local sites"} to specialized deployments at ${city.landmarks.slice(-1)[0] || "regional events"}, our ${city.name} operations centre coordinates every officer, every route and every incident report through the same GuardTour digital platform used province-wide. Coordinates: ${city.coords}.`,
  ];

  const cityFaq = [
    {
      q: `Do you provide security services throughout all of ${city.name}?`,
      a: `Yes. Our ${city.name} team covers every neighbourhood, industrial park, retail corridor and event venue within the ${city.region} region. We can also extend patrol routes to surrounding rural properties as required.`,
    },
    {
      q: `How quickly can you deploy an officer in ${city.name}?`,
      a: `Standard deployment in ${city.name} is within 4 hours of contract execution. Emergency alarm response averages 12–18 minutes depending on time-of-day and location within the community.`,
    },
    {
      q: `Are your ${city.name} guards licensed and insured?`,
      a: `Every officer we deploy in ${city.name} holds a valid NB Class 2 Security Worker license, First Aid & CPR certification, and is covered under our $5,000,000 Commercial General Liability policy.`,
    },
  ];

  return (
    <div data-testid={IDS.cities.detail(city.slug)}>
      <Seo
        title={`Security Guards in ${city.name}, NB | Maze Security`}
        description={`Licensed uniformed guards, mobile patrol, event & alarm response in ${city.name}, New Brunswick (${city.region}). Deploy in 4 hours. Bilingual. Insured.`}
        path={`/cities/${city.slug}`}
      />
      {/* Hero */}
      <section className="relative pt-32 md:pt-40 pb-16 md:pb-24 border-b border-border overflow-hidden">
        <div className="absolute inset-0 opacity-40">
          <img
            src={city.hero}
            alt={city.name}
            className="w-full h-full object-cover grayscale contrast-125"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-background via-background/70 to-background/30" />
        </div>

        <div className="relative mx-auto max-w-[1600px] px-6 md:px-12">
          <Link
            to="/cities"
            className="font-mono text-[10px] tracking-[0.3em] uppercase text-muted-foreground hover:text-primary transition-colors mb-8 inline-block"
          >
            ← {lang === "fr" ? "Toutes les villes" : "All Cities"}
          </Link>
          <div className="grid grid-cols-1 md:grid-cols-12 gap-8 items-end">
            <div className="md:col-span-8">
              <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-muted-foreground mb-6 flex items-center gap-3">
                <MapPin size={12} strokeWidth={1.5} />
                {city.region} · {city.coords}
              </div>
              <motion.h1
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 1, ease: [0.76, 0, 0.24, 1] }}
                className="font-serif font-light text-5xl md:text-9xl leading-[0.9] tracking-tighter"
              >
                {lang === "fr" ? "Agents de sécurité à" : "Security guards in"} <br />
                <i className="text-primary">{city.name}</i>
              </motion.h1>
            </div>
            <div className="md:col-span-4">
              <div className="font-mono text-xs tracking-widest space-y-3">
                <div className="flex justify-between border-b border-border pb-3">
                  <span className="text-muted-foreground uppercase">{lang === "fr" ? "Population" : "Population"}</span>
                  <span>{city.population}</span>
                </div>
                <div className="flex justify-between border-b border-border pb-3">
                  <span className="text-muted-foreground uppercase">{lang === "fr" ? "Région" : "Region"}</span>
                  <span>{city.region}</span>
                </div>
                <div className="flex justify-between border-b border-border pb-3">
                  <span className="text-muted-foreground uppercase">{lang === "fr" ? "Statut" : "Status"}</span>
                  <span className="text-primary">{lang === "fr" ? "Patrouille active" : "Active Patrol"}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Intro + sectors */}
      <section className="border-b border-border">
        <div className="mx-auto max-w-[1600px] px-6 md:px-12 py-24 grid grid-cols-1 md:grid-cols-12 gap-12">
          <div className="md:col-span-7 space-y-8">
            <p className="font-serif italic text-2xl md:text-3xl leading-snug text-foreground/90 max-w-2xl">
              {city.intro}
            </p>
            {longContent.map((p, i) => (
              <p
                key={i}
                className="text-base md:text-lg leading-relaxed text-foreground/80 max-w-2xl"
              >
                {p}
              </p>
            ))}
          </div>
          <aside className="md:col-span-5">
            <div className="md:sticky md:top-32 border border-border p-8 md:p-10 bg-[hsl(0_0%_6%)]">
              <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-muted-foreground mb-4">
                [ {lang === "fr" ? "Secteurs que nous protégeons" : "Sectors we protect"} ]
              </div>
              <ul className="space-y-3 mb-8">
                {city.sectors.map((s) => (
                  <li
                    key={s}
                    className="font-serif text-xl border-b border-border pb-3"
                  >
                    {s}
                  </li>
                ))}
              </ul>
              <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-muted-foreground mb-3">
                [ {lang === "fr" ? "Points de repère" : "Landmarks"} ]
              </div>
              <ul className="space-y-2 text-foreground/80">
                {city.landmarks.map((l) => (
                  <li key={l} className="font-sans text-sm">
                    · {l}
                  </li>
                ))}
              </ul>
              <div className="mt-8 pt-8 border-t border-border">
                <Link
                  to="/contact#quote"
                  data-testid="city-detail-cta"
                  className="inline-flex items-center gap-3 bg-primary text-primary-foreground px-6 py-4 font-mono text-xs tracking-[0.2em] uppercase hover:bg-foreground hover:text-background transition-colors"
                >
                  {lang === "fr" ? "Déployer à" : "Deploy in"} {city.name} <ArrowRight size={14} />
                </Link>
              </div>
            </div>
          </aside>
        </div>
      </section>

      {/* Services X City internal-link grid — HUGE for SEO */}
      <section className="border-b border-border">
        <div className="mx-auto max-w-[1600px] px-6 md:px-12 py-24">
          <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-muted-foreground mb-6">
            [ {SERVICES.length} Services in {city.name} ]
          </div>
          <h3 className="font-serif text-3xl md:text-6xl leading-tight tracking-tight mb-12">
            All Services · <i className="text-primary">{city.name}</i>
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 border-t border-l border-border">
            {SERVICES.map((s, i) => (
              <Link
                key={s.slug}
                to={`/services/${s.slug}`}
                data-testid={`city-service-${s.slug}`}
                className="border-r border-b border-border p-8 hover:bg-[hsl(0_0%_7%)] transition-colors group"
              >
                <div className="flex justify-between items-start mb-3">
                  <span className="font-mono text-[10px] tracking-widest text-muted-foreground">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <ArrowRight
                    size={14}
                    strokeWidth={1.2}
                    className="group-hover:text-primary group-hover:translate-x-1 transition-all"
                  />
                </div>
                <div className="font-serif text-xl md:text-2xl leading-tight group-hover:text-primary transition-colors">
                  {s.name} in {city.name}
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* City-specific FAQ */}
      <section className="border-b border-border bg-[hsl(0_0%_5%)]">
        <div className="mx-auto max-w-[1600px] px-6 md:px-12 py-24">
          <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-muted-foreground mb-6">
            [ FAQ · {city.name} ]
          </div>
          <h3 className="font-serif text-3xl md:text-5xl leading-tight tracking-tight mb-12">
            Common questions about security in <i className="text-primary">{city.name}</i>
          </h3>
          <div className="space-y-8 max-w-4xl">
            {cityFaq.map((f, i) => (
              <div key={i} className="border-b border-border pb-8">
                <div className="font-serif text-xl md:text-2xl leading-tight mb-3">
                  {f.q}
                </div>
                <p className="text-foreground/75 leading-relaxed max-w-2xl">
                  {f.a}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <EditorialMarquee />

      {/* Case Study cross-link */}
      {caseStudy && (
        <section className="border-b border-border">
          <div className="mx-auto max-w-[1600px] px-6 md:px-12 py-16 md:py-20">
            <Link
              to={`/case-studies/${caseStudy.slug}`}
              data-testid={`city-case-study-link-${city.slug}`}
              className="group grid grid-cols-1 md:grid-cols-12 gap-8 items-center border border-border p-8 md:p-12 hover:bg-[hsl(0_0%_7%)] transition-colors"
            >
              <div className="md:col-span-8">
                <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-muted-foreground mb-3 flex items-center gap-3">
                  <FileText size={12} strokeWidth={1.5} />
                  {lang === "fr" ? "Rapport d'incident · Aseptisé" : "Incident Report · Sanitized"}
                  <span>·</span><span>{caseStudy.date}</span>
                </div>
                <h3 className="font-serif text-3xl md:text-5xl leading-tight tracking-tight group-hover:text-primary transition-colors">
                  {caseStudy.threat} <i className="text-primary">— {city.name}.</i>
                </h3>
              </div>
              <div className="md:col-span-4 flex md:justify-end">
                <span className="inline-flex items-center gap-3 font-mono text-xs tracking-[0.2em] uppercase group-hover:text-primary transition-colors">
                  {lang === "fr" ? "Lire le cas" : "Read Case"} <ArrowRight size={14} />
                </span>
              </div>
            </Link>
          </div>
        </section>
      )}

      {/* Nearby cities */}
      <section className="border-b border-border">
        <div className="mx-auto max-w-[1600px] px-6 md:px-12 py-24">
          <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-muted-foreground mb-6">
            [ Nearby ]
          </div>
          <h3 className="font-serif text-3xl md:text-5xl leading-tight tracking-tight mb-10">
            Other <i className="text-primary">NB Cities</i>
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {nearby.map((c) => (
              <Link
                key={c.slug}
                to={`/cities/${c.slug}`}
                data-testid={`related-city-${c.slug}`}
                className="border border-border p-8 hover:bg-[hsl(0_0%_7%)] transition-colors group"
              >
                <div className="flex items-start justify-between mb-3">
                  <span className="font-mono text-[10px] tracking-widest text-muted-foreground">
                    {c.region}
                  </span>
                  <ArrowRight
                    size={16}
                    strokeWidth={1.2}
                    className="group-hover:translate-x-1 group-hover:text-primary transition-all"
                  />
                </div>
                <h4 className="font-serif text-2xl md:text-3xl leading-tight group-hover:text-primary transition-colors">
                  {c.name}
                </h4>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <QuoteForm defaultCity={city.name} />
    </div>
  );
}
